import { z } from "zod";

import { baseCollectionSchema } from "./collection/baseCollectionSchema";
import { triviaLeaderboardSchema } from "./home/triviaLeaderboardSchema";
import { triviaGameSchema } from "./home/triviaSchema";
import { imagesSchema } from "./imagesSchema";
import { basePrayerSchema } from "./prayer/basePrayerSchema";
import { wordSchema } from "./wordSchema";

export enum RoutineItemReferenceType {
  COLLECTION = "collection",
  /** @deprecated */
  CHAPTER = "chapter",
  PRAYER = "prayer",
  CHALLENGE = "challenge",
  /** @deprecated */
  DAILY = "daily",
  REMINDER = "reminder",
  ACTIVITY = "activity",
}

const routineSessionSchema = z.object({
  id: z.number(),
  is_completed: z.boolean(),
  prayer: basePrayerSchema
    .pick({
      collection_id: true,
      detailed_desc: true,
      has_access: true,
      id: true,
      is_available: true,
      is_completed: true,
      is_download_enabled: true,
      is_favorite: true,
      is_redeemed: true,
      is_song: true,
      label_desc: true,
      lengths: true,
      shareable_quote: true,
      shareable_url: true,
      supertitle: true,
      title: true,
      order: true,
      prompt: true,
    })
    .nullable(),
  prayer_id: z.number().nullable(),
  routine_item_id: z.number(),
  timestamp: z.string(),
});

export const baseRoutineItemSchema = z.object({
  id: z.number(),
  index: z.number(),
  session: routineSessionSchema.nullable(),
});

export enum ActivityReferenceIDs {
  Trivia = 1,
  TheWord = 2,
  Categories = 3,
}

export const routineItemSchema = z.discriminatedUnion("reference_type", [
  baseRoutineItemSchema.extend({
    reference_type: z.literal(RoutineItemReferenceType.CHALLENGE),
    reference: baseCollectionSchema,
    reference_id: z.number(),
  }),
  baseRoutineItemSchema.extend({
    reference_type: z.literal(RoutineItemReferenceType.COLLECTION),
    reference: baseCollectionSchema,
    reference_id: z.number(),
  }),
  baseRoutineItemSchema.extend({
    reference_type: z.literal(RoutineItemReferenceType.PRAYER),
    reference: basePrayerSchema,
    reference_id: z.number(),
  }),
  z.discriminatedUnion("reference_id", [
    baseRoutineItemSchema.extend({
      reference_type: z.literal(RoutineItemReferenceType.ACTIVITY),
      reference: z.object({
        action_type: z.literal("trivia"),
        title: z.string(),
        subtitle: z.string(),
        images: imagesSchema,
        game: triviaGameSchema.nullable(),
        leaderboard: triviaLeaderboardSchema.nullable(),
      }),
      reference_id: z.literal(ActivityReferenceIDs.Trivia),
    }),
    baseRoutineItemSchema.extend({
      reference_type: z.literal(RoutineItemReferenceType.ACTIVITY),
      reference: z.object({
        action_type: z.literal("the_word"),
        title: z.string(),
        subtitle: z.string(),
        images: imagesSchema,
        game: wordSchema.nullable(),
      }),
      reference_id: z.literal(ActivityReferenceIDs.TheWord),
    }),
  ]),
  baseRoutineItemSchema.extend({
    reference_type: z.literal(RoutineItemReferenceType.REMINDER),
    reference: z.object({
      title: z.string(),
      subtitle: z.string(),
      images: imagesSchema,
    }),
    reference_id: z.number(),
  }),
]);

export type RoutineItem = z.infer<typeof routineItemSchema>;
